import { useCallback, useEffect, useState } from 'react';
import {
  fetchAvailableSeasons,
  fetchAvailableTracks,
  fetchRaceResults,
  fetchSessionResults,
} from '../../services/resultsService.js';

import ScreenHeader from '../common/ScreenHeader.jsx';
import Skeleton from '../common/Skeleton.jsx';

import TrackPicker from './TrackPicker.jsx';
import RaceMeta from './RaceMeta.jsx';
import ResultsAccordion from './ResultsAccordion.jsx';
import StandingsView from './StandingsView.jsx';
import SeasonOverview from './SeasonOverview.jsx';

/**
 * ResultsScreen — live race results browser.
 *
 * Flow:
 *   1. User selects a year → fetches the season's race calendar from
 *      Jolpica-F1 (free Ergast successor, 1950–present; UI floor is 2000).
 *   2. Tracks are listed in calendar order (earliest race first).
 *   3. Default selection = most recent concluded race.
 *   4. On track select → fetches full classification incl. gaps, weather,
 *      pole, fastest lap.
 *
 * Session dropdown note: Jolpica has no result data for practice sessions
 * (schedule times only) — the dropdown only offers Qualifying / Sprint /
 * Race, and Sprint only appears for weekends that actually have one.
 */

export default function ResultsScreen() {
  const seasons = fetchAvailableSeasons();
  const [selectedYear, setSelectedYear] = useState(seasons[0]);
  const [viewMode, setViewMode] = useState('races'); // 'races' | 'standings'
  const [tracks, setTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('all'); // 'all' or a track id
  const [state, setState] = useState({ status: 'idle', data: null, error: null });

  // Session state (Qualifying, Sprint)
  const [selectedSession, setSelectedSession] = useState('race'); // 'race' | 'qualifying' | 'sprint'
  const [sessionState, setSessionState] = useState({ status: 'idle', data: null });

  // Load tracks when year changes.
  useEffect(() => {
    const ctrl = new AbortController();
    setTracksLoading(true);
    setSelectedId('all');
    setState({ status: 'idle', data: null, error: null });

    (async () => {
      try {
        const list = await fetchAvailableTracks(selectedYear, { signal: ctrl.signal });
        if (!ctrl.signal.aborted) {
          setTracks(list);
          setTracksLoading(false);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to load tracks', err);
          setTracksLoading(false);
        }
      }
    })();
    return () => ctrl.abort();
  }, [selectedYear]);

  // Load results when track changes.
  const loadResults = useCallback(async (trackId, signal, trackList) => {
    if (!trackId) return;
    setState({ status: 'loading', data: null, error: null });
    try {
      const data = await fetchRaceResults(trackId, trackList, { signal });
      if (data?._status === 'cancelled') {
        setState({ status: 'cancelled', data: null, error: null });
      } else if (data?._status === 'upcoming') {
        setState({ status: 'upcoming', data, error: null });
      } else {
        setState({ status: 'success', data, error: null });
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setState({ status: 'error', data: null, error: err });
    }
  }, []);

  useEffect(() => {
    if (!selectedId || selectedId === 'all') return;
    const ctrl = new AbortController();
    loadResults(selectedId, ctrl.signal, tracks);
    setSelectedSession('race');
    setSessionState({ status: 'idle', data: null });
    return () => ctrl.abort();
  }, [selectedId, loadResults, tracks]);

  // Load session-specific data when the session dropdown changes.
  useEffect(() => {
    if (selectedSession === 'race' || !selectedSession) return;
    const track = tracks.find((t) => t.id === selectedId);
    if (!track) return;
    const ctrl = new AbortController();
    setSessionState({ status: 'loading', data: null });
    (async () => {
      try {
        const data = await fetchSessionResults(track, selectedSession, { signal: ctrl.signal });
        if (!ctrl.signal.aborted) {
          setSessionState({ status: 'success', data });
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setSessionState({ status: 'error', data: null });
        }
      }
    })();
    return () => ctrl.abort();
  }, [selectedSession, selectedId, tracks]);

  const selectedTrack = tracks.find((t) => t.id === selectedId);
  const sessionOptions = selectedTrack ? buildSessionOptions(selectedTrack) : [];

  // Sunday's weather, when the race payload actually carries it. The sprint
  // panel falls back to this and used to test `state.data?.weather?.air_temp_c
  // !== '-'` before dereferencing `state.data.weather` — but when the race
  // request is still in flight or has failed, `state.data` is null, that
  // comparison is `undefined !== '-'` (true), and the render threw. It took the
  // whole app down, since nothing above it catches. Resolve the object once and
  // let the JSX branch on the object itself.
  const raceWeather =
    state.data?.weather && state.data.weather.air_temp_c !== '-' ? state.data.weather : null;

  return (
    <div className="flex flex-col px-5 pb-8">
      <ScreenHeader
        eyebrow="LIVE DATA"
        title="Results"
        subtitle={
          viewMode === 'standings'
            ? `${selectedYear} Championship Standings`
            : selectedId === 'all'
              ? `${selectedYear} Season Overview`
              : selectedTrack
                ? `${selectedTrack.flag} ${selectedTrack.circuit_short_name} · ${selectedTrack.country_name}`
                : 'Select a race'
        }
      />

      {/* Races / Standings toggle */}
      <div className="mb-3 flex items-center gap-1 rounded-xl bg-bg-card p-1 ring-1 ring-stroke">
        <button
          onClick={() => setViewMode('races')}
          className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold transition-all ${
            viewMode === 'races'
              ? 'bg-f1-red text-white shadow-glow-red-soft'
              : 'text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Races
        </button>
        <button
          onClick={() => setViewMode('standings')}
          className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold transition-all ${
            viewMode === 'standings'
              ? 'bg-f1-red text-white shadow-glow-red-soft'
              : 'text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Standings
        </button>
      </div>

      {/* Year selector + Session selector */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          className="pressable appearance-none rounded-xl bg-bg-card px-3 py-2 pr-7 font-mono text-xs font-bold tabular-nums text-ink-primary ring-1 ring-stroke transition-all focus:ring-f1-red/60"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23a1a1aa' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
          }}
        >
          {seasons.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>

        {/* Session dropdown — right-aligned, only when a specific race is selected */}
        {selectedId !== 'all' && sessionOptions.length > 1 ? (
          <select
            value={selectedSession}
            onChange={(e) => setSelectedSession(e.target.value)}
            className="ml-auto appearance-none rounded-xl bg-bg-card px-3 py-2 pr-7 text-xs font-bold text-ink-primary ring-1 ring-stroke focus:ring-f1-red/60"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23a1a1aa' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
            }}
          >
            {sessionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : null}

        {selectedId === 'all' && !tracksLoading && tracks.length > 0 ? (
          <span className="ml-auto text-2xs font-semibold tabular-nums text-ink-tertiary">
            {tracks.filter((t) => t.isConcluded && !t.is_cancelled).length} / {tracks.filter((t) => !t.is_cancelled).length} races
          </span>
        ) : null}
      </div>

      {/* Standings view */}
      {/* key={year} — see the effect note in StandingsView; remounting is how
          its loading state resets when the season changes. */}
      {viewMode === 'standings' ? (
        <StandingsView key={selectedYear} year={selectedYear} />
      ) : null}

      {/* Races view */}
      {viewMode === 'races' ? (
        <>
          {/* Track picker — hidden when "All Races" is selected */}
          {selectedId !== 'all' ? (
            tracksLoading ? (
              <Skeleton className="h-16 w-full" rounded="rounded-2xl" />
            ) : tracks.length > 0 ? (
              <TrackPicker tracks={tracks} value={selectedId} onChange={setSelectedId} showAllOption />
            ) : (
              <div className="rounded-2xl bg-bg-card px-4 py-6 text-center text-sm text-ink-secondary ring-1 ring-stroke">
                No race data available for {selectedYear}.
              </div>
            )
          ) : null}

          {/* Season overview when "All" is selected */}
          {selectedId === 'all' && !tracksLoading ? (
            <SeasonOverview key={selectedYear} tracks={tracks} year={selectedYear} onSelectRace={setSelectedId} />
          ) : null}

          {/* Race results (when session = 'race') */}
          {selectedId !== 'all' && selectedSession === 'race' ? (
            <>
              {state.status === 'loading' ? <ResultsSkeleton /> : null}

              {state.status === 'cancelled' ? (
                <div className="mt-6 flex flex-col items-center justify-center rounded-2xl bg-bg-card px-6 py-12 text-center ring-1 ring-stroke animate-fade-in">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-ink-muted/10 ring-1 ring-ink-muted/30">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ink-tertiary">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-ink-primary">Race Cancelled</h3>
                  <p className="mt-2 max-w-xs text-sm leading-relaxed text-ink-secondary">
                    Sorry, this race has been cancelled! Select another circuit from the list above.
                  </p>
                </div>
              ) : null}

              {state.status === 'upcoming' ? (
                <UpcomingRaceCard daysUntil={state.data?.days_until} />
              ) : null}

              {state.status === 'error' ? (
                <div className="mt-6 flex flex-col items-center justify-center rounded-2xl bg-bg-card px-6 py-12 text-center ring-1 ring-stroke animate-fade-in">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-f1-red/10 ring-1 ring-f1-red/30">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ink-tertiary">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <circle cx="12" cy="16" r="0.5" fill="currentColor" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-ink-primary">Something went wrong</h3>
                  <p className="mt-2 max-w-xs text-sm leading-relaxed text-ink-secondary">
                    Race data could not be loaded. Check your connection and try again.
                  </p>
                  <button
                    onClick={() => loadResults(selectedId, new AbortController().signal, tracks)}
                    className="pressable mt-5 inline-flex items-center gap-2 rounded-full bg-f1-red px-5 py-2.5 text-sm font-semibold text-white shadow-glow-red-soft transition-colors hover:bg-f1-redBright"
                  >
                    Retry
                  </button>
                </div>
              ) : null}

              {state.status === 'success' && state.data ? (
                <div key={selectedId} className="animate-fade-in">
                  <RaceMeta payload={state.data} />
                  <ResultsAccordion results={state.data.results} />
                  {state.data._provisional ? (
                    <p className="mt-4 rounded-xl bg-signal-amber/10 px-3 py-2 text-center text-2xs leading-relaxed text-signal-amber ring-1 ring-signal-amber/30">
                      Provisional — on-track order at the flag. Post-race penalties
                      may not be reflected.
                    </p>
                  ) : null}
                  <p className="mt-4 text-center text-[10px] uppercase tracking-widest-plus text-ink-muted">
                    {sourceLabel(state.data._source)} · Weather via OpenF1/Open-Meteo
                  </p>
                </div>
              ) : null}
            </>
          ) : null}

          {/* Session results (Qualifying, Sprint) */}
          {selectedId !== 'all' && selectedSession !== 'race' ? (
            <>
              {sessionState.status === 'loading' ? <ResultsSkeleton /> : null}
              {sessionState.status === 'success' && sessionState.data ? (
                <div className="animate-fade-in">
                  {sessionState.data.isSprint && sessionState.data.pole ? (
                    <div className="mt-4 overflow-hidden rounded-2xl bg-bg-card ring-1 ring-stroke shadow-card">
                      <div className="relative grid grid-cols-2 gap-px bg-stroke">
                        <div className="bg-bg-card px-3 py-3">
                          <div className="text-[10px] font-bold uppercase tracking-widest-plus text-ink-tertiary">Sprint Pole</div>
                          <div className="mt-1 text-sm font-semibold text-ink-primary">{sessionState.data.pole.driver_name}</div>
                          {/* Never a bare dash. 2023+ sprint grids come from a
                              session Jolpica has no results for, so no lap time
                              exists; earlier seasons have one, so a blank there
                              means the lookup failed and must not claim otherwise. */}
                          {sessionState.data.pole.lap_time ? (
                            <div className="font-mono text-2xs tabular-nums text-f1-red">{sessionState.data.pole.lap_time}</div>
                          ) : (
                            <div className="text-2xs text-ink-muted">
                              {sessionState.data.pole.lap_time_source === 'sprint-qualifying'
                                ? 'Set in sprint qualifying'
                                : 'Lap time unavailable'}
                            </div>
                          )}
                        </div>
                        {sessionState.data.fastestLap ? (
                          <div className="bg-bg-card px-3 py-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest-plus text-ink-tertiary">Sprint FL</div>
                            <div className="mt-1 text-sm font-semibold text-ink-primary">{sessionState.data.fastestLap.driver_name}</div>
                            <div className="font-mono text-2xs tabular-nums text-signal-purple">{sessionState.data.fastestLap.lap_time}</div>
                          </div>
                        ) : raceWeather ? (
                          // Sunday's weather, shown as a stand-in when the sprint
                          // has no fastest-lap data. Labelled explicitly — it is
                          // NOT the weather during the sprint itself.
                          <div className="bg-bg-card px-3 py-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest-plus text-ink-tertiary">Race weather</div>
                            <div className="mt-1 text-sm font-semibold text-ink-primary">{raceWeather.conditions}</div>
                            <div className="font-mono text-2xs tabular-nums text-ink-secondary">Air {raceWeather.air_temp_c}°C · Track {raceWeather.track_temp_c}°C</div>
                          </div>
                        ) : <div className="bg-bg-card" />}
                      </div>
                    </div>
                  ) : null}
                  <ResultsAccordion results={sessionState.data.results} />
                  <p className="mt-4 text-center text-[10px] uppercase tracking-widest-plus text-ink-muted">
                    {sessionState.data.session?.type ?? 'Session'} · sourced from Jolpica-F1
                  </p>
                </div>
              ) : null}
              {sessionState.status === 'error' ? (
                <div className="mt-6 rounded-2xl bg-bg-card px-4 py-8 text-center text-sm text-ink-secondary ring-1 ring-stroke">
                  Session data could not be loaded.
                </div>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * sourceLabel — the footer used to hardcode "Jolpica-F1" regardless of which
 * tier actually served the data, which was misleading whenever the fallback
 * chain kicked in.
 */
function sourceLabel(source) {
  if (source === 'livetiming') return 'Classification from F1 live timing';
  if (source === 'openf1-backup') return 'Classification reconstructed from OpenF1';
  return 'Classification from Jolpica-F1';
}

function UpcomingRaceCard({ daysUntil: days }) {
  // Computed by resultsService when the payload was built — see daysUntil()
  // there for why this is not derived at render time.
  const daysUntil = days ?? '?';

  return (
    <div className="mt-6 flex flex-col items-center justify-center rounded-2xl bg-bg-card px-6 py-12 text-center ring-1 ring-stroke animate-fade-in">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-ink-muted/10 ring-1 ring-ink-muted/30">
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none" className="text-ink-tertiary">
          {/* Calendar outline */}
          <rect x="3" y="6" width="28" height="25" rx="3" stroke="currentColor" strokeWidth="1.8" />
          {/* Top hooks */}
          <line x1="10" y1="3" x2="10" y2="9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="24" y1="3" x2="24" y2="9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          {/* Header bar */}
          <line x1="3" y1="13" x2="31" y2="13" stroke="currentColor" strokeWidth="1.8" />
          {/* Days number */}
          <text x="17" y="25" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="700" fontFamily="ui-monospace, monospace">
            {daysUntil}
          </text>
        </svg>
      </div>
      <h3 className="text-lg font-bold text-ink-primary">Race Upcoming</h3>
      <p className="mt-2 max-w-xs text-sm leading-relaxed text-ink-secondary">
        {daysUntil === 0
          ? "Race day! Results will appear here after the chequered flag."
          : `${daysUntil} day${daysUntil !== 1 ? 's' : ''} to go — stay tuned! Results will appear here after the chequered flag.`}
      </p>
    </div>
  );
}

function ResultsSkeleton() {
  return (
    <div className="mt-4 space-y-3" aria-hidden="true">
      <Skeleton className="h-36 w-full" rounded="rounded-2xl" />
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-14 w-full" rounded="rounded-xl" />
      <Skeleton className="h-14 w-full" rounded="rounded-xl" />
      <Skeleton className="h-14 w-full" rounded="rounded-xl" />
      <Skeleton className="h-14 w-full" rounded="rounded-xl" />
      <Skeleton className="h-14 w-full" rounded="rounded-xl" />
    </div>
  );
}

/**
 * buildSessionOptions — Qualifying always available (2003+ has full Q1-3,
 * earlier seasons may come back sparse/empty and just render "no data").
 * Sprint only offered for weekends that actually have one.
 */
function buildSessionOptions(track) {
  const opts = [{ value: 'qualifying', label: 'Qualifying' }];
  if (track.hasSprint) opts.push({ value: 'sprint', label: 'Sprint' });
  opts.push({ value: 'race', label: 'Race' });
  return opts;
}
